import { pubText, pubFormat } from "../../i18n/publicTranslation";
import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, Slider } from '@mui/material';
import { PlayArrow, Pause, VolumeUp, VolumeOff } from '@mui/icons-material';

interface MusicPlayerProps {
  src: string;
  title?: string;
  subtitle?: string;
}

const MusicPlayer: React.FC<MusicPlayerProps> = ({
  src,
  title = "Tonight I'll Have It All",
  subtitle = ""
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const time = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const vol = Array.isArray(newValue) ? newValue[0] : newValue;
    audio.volume = vol;
    setVolume(vol);
    setIsMuted(vol === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

    return (
      <Box sx={{
        background: 'linear-gradient(135deg, #D8A363, #E8B373)',
        borderRadius: '50px',
        padding: '8px',
        boxShadow: '0 6px 24px rgba(216, 163, 99, 0.3)',
        maxWidth: '480px',
        width: '90%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0))',
          borderRadius: '20px 20px 0 0',
        }
      }}>
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          title={pubText(title)}
          aria-label={pubFormat(
            "Music player for __VALUE_0____VALUE_1__",
            [title, subtitle ? ` - ${subtitle}` : '']
          )}
        />
        {/* Controls Row */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          zIndex: 1,
          position: 'relative'
        }}>
          {/* Play Button */}
          <Box
            onClick={togglePlayPause}
            sx={{
              backgroundColor: '#fff',
              color: '#D8A363',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            {pubText(isPlaying ? (
              <Box sx={{
                display: 'flex',
                gap: '2px',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {pubText([...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: '2px',
                      borderRadius: '1px',
                      backgroundColor: '#D8A363',
                      animation: 'wave-animation 1.2s infinite',
                      animationDelay: `${i * 0.15}s`,
                      height: '8px',
                      '@keyframes wave-animation': {
                        '0%, 100%': {
                          height: '4px'
                        },
                        '50%': {
                          height: '12px'
                        }
                      }
                    }}
                  />
                )))}
              </Box>
            ) : (
              <PlayArrow sx={{ fontSize: 20, color: '#D8A363', marginInlineStart: 0.3 }} />
            ))}
          </Box>

          {/* Time Display */}
          <Typography
            variant="caption"
            sx={{
              color: '#fff',
              fontSize: '0.75rem',
              minWidth: '65px',
              fontFamily: 'monospace',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}
          >
            {pubText(formatTime(currentTime))} / {pubText(formatTime(duration))}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ flex: 1 }}>
            <Slider
              value={currentTime}
              max={duration || 100}
              onChange={handleSeek}
              sx={{
                color: '#fff',
                verticalAlign: 'middle',
                height: 4,
                '& .MuiSlider-track': {
                  backgroundColor: '#fff',
                  border: 'none',
                  boxShadow: '0 1px 4px rgba(255, 255, 255, 0.3)'
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  border: 'none'
                },
                '& .MuiSlider-thumb': {
                  backgroundColor: '#fff',
                  width: 12,
                  height: 12,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }
                }
              }}
            />
          </Box>

          {/* Volume Control */}
          <IconButton
            onClick={toggleMute}
            sx={{
              color: '#fff',
              padding: '6px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {pubText(isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />)}
          </IconButton>
        </Box>
      </Box>
    );
};

export default MusicPlayer;